import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { sortBy } from 'lodash';
import { useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import {
  EquipmentListsActions,
  ProcedureEquipmentListsList,
  ProcedureEquipmentListForm,
  EquipmentListItemForm,
  EquipmentListItemsList,
  EquipmentListsBreadcrumbs,
  AddKitsModal,
  AddItemsModal,
  TitleForm,
} from '../../../components/procedures/procedure-equipment-lists';
import Input from '../../../components/shared/Input';
import Modal, { ConfirmationModal } from '../../../components/shared/modal';

import {
  getProcedureById,
  createProcedureEquipmentList,
  subscribeToProcedureEquipmentLists,
  updateProcedureEquipmentList,
  deleteProcedureEquipmentList, reorderEquipmentLists,
} from '../../../actions/proceduresActions';
import { getKits } from '../../../actions/kitsActions';
import { getItemsByIds } from '../../../actions/itemsActions';

import { useLoading, useAlert } from '../../../hooks';

import { kitPreferenceItemTypes, procedureEquipmentListItemTypes } from '../../../constants/enums';

const ProcedureEquipmentListsPage = () => {
  const { id } = useParams();
  const procedureId = id.replace(':', '');

  const dispatch = useDispatch();
  const tenant = useSelector((state) => state.tenant.currentTenant);
  const deletePermission = useSelector((state) => state.user?.currentUser?.deletePermission);
  const kits = useSelector((state) => state.kits.list);

  const { loading, startLoading, stopLoading } = useLoading(false);
  const { loading: deleting, startLoading: startDeleting, stopLoading: stopDeleting } = useLoading(false);
  const { loading: duplicateLoading, startLoading: startDuplicateLoading, stopLoading: stopDuplicateLoading } = useLoading(false);
  const { showAlert } = useAlert();

  const [procedure, setProcedure] = useState(null);
  const [search, setSearch] = useState('');

  const [equipmentLists, setEquipmentLists] = useState([]);
  const [selectedEquipmentList, selectEquipmentList] = useState(null);
  const [selectedItem, selectItem] = useState(null);
  const [itemsList, setItemsList] = useState([]);

  const [equipmentListModal, toggleEquipmentListModal] = useState(false);
  const [deleteEquipmentListModal, toggleDeleteEquipmentListModal] = useState(false);
  const [deleteItemModal, toggleDeleteItemModal] = useState(false);
  const [duplicateModalOpen, toggleDuplicateModal] = useState(false);

  const [kitsModalOpen, toggleKitsModal] = useState(false);
  const [itemsModalOpen, toggleItemModal] = useState(false);
  const [titleModalOpen, toggleTitleModal] = useState(false);

  useEffect(() => {
    const unsubscribe = dispatch(
      subscribeToProcedureEquipmentLists(procedureId, (lists) => {
        setEquipmentLists(sortBy(lists, 'orderNumber'));
      })
    );

    onLoad().catch();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [procedureId, dispatch]);

  useEffect(() => {
    if (selectedEquipmentList && selectedEquipmentList.items) {
      fetchItems(selectedEquipmentList.items).catch();
    }
  }, [selectedEquipmentList]);

  const onLoad = async () => {
    try {
      const res = await dispatch(getProcedureById(procedureId));
      if (res) {
        setProcedure(res);
      }
      await Promise.all([
        dispatch(getKits())
      ]);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchItems = async (items) => {
    try {
      const res = await dispatch(getItemsByIds(items?.filter((i) => i.type === kitPreferenceItemTypes.ITEM.value).map((i) => i.value)));
      setItemsList(res || []);
    } catch (err) {
      console.error(err)
    }
  };

  const onAddClick = (type) => {
    if (type === procedureEquipmentListItemTypes.ITEM.value) {
      toggleItemModal(true);
    } else if (type === procedureEquipmentListItemTypes.KIT.value) {
      toggleKitsModal(true);
    } else if (type === procedureEquipmentListItemTypes.TITLE.value) {
      toggleTitleModal(true);
    } else {
      toggleEquipmentListModal(true);
    }
  };

  const onSubmitEquipmentList = async (formObj, mode) => {
    startLoading();

    const equipmentListObj = {
      name: formObj.name || '',
      subtitle: formObj.subtitle || '',
      description: '',
    };

    if (mode === 'create') {
      equipmentListObj.orderNumber = equipmentLists.length + 1;
      equipmentListObj.items = [];
    }

    if (mode === 'update') {
      equipmentListObj.active = !!formObj.active;
      equipmentListObj.description = formObj.description || '';
    }

    try {
      if (mode === 'update') {
        await dispatch(updateProcedureEquipmentList(procedureId, selectedEquipmentList.id, equipmentListObj));
        selectEquipmentList(formObj);
      } else {
        await dispatch(createProcedureEquipmentList(procedureId, equipmentListObj));
      }
      stopLoading();
      toggleEquipmentListModal(false);
      showAlert('success', `Equipment list has been successfully ${mode === 'update' ? 'updated' : 'created'}`);
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  const onDeleteEquipmentList = async () => {
    startDeleting();

    try {
      await dispatch(deleteProcedureEquipmentList(procedureId, selectedEquipmentList.id));
      await Promise.all(
        equipmentLists
          .filter((list) => list.orderNumber > selectedEquipmentList.orderNumber)
          .map((list) => dispatch(updateProcedureEquipmentList(list.id, { orderNumber: list.orderNumber - 1 })))
      );
      toggleDeleteEquipmentListModal(false);
      selectEquipmentList(null);
      selectItem(null);
      showAlert('success', 'Equipment list has been successfully deleted');
    } catch (err) {
      console.error(err);
    } finally {
      stopDeleting();
    }
  };

  // Items

  const onAddItems = async (ids, type) => {
    startLoading();

    try {
      const items = [...selectedEquipmentList?.items];

      ids.forEach((id, index) => {
        const item = {
          id: uuid().substring(0, 20),
          orderNumber: items?.length + index + 1,
          type,
          value: id,
          preSelect: true,
          mandatory: false,
          fixedQuantity: false,
          min: null,
          max: null,
        };
        items.push(item);
      });

      await dispatch(updateProcedureEquipmentList(procedureId, selectedEquipmentList.id, { ...selectedEquipmentList, items }));
      selectEquipmentList({ ...selectedEquipmentList, items });

      stopLoading();
      toggleKitsModal(false);
      toggleItemModal(false);
      showAlert('success', `${procedureEquipmentListItemTypes[type]?.label}s have been successfully added`);
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  const onAddTitle = async (formObj) => {
    startLoading();

    const items = [...selectedEquipmentList?.items];
    const newItem = {
      id: uuid().substring(0, 20),
      orderNumber: items?.length + 1,
      title: formObj.title || '',
      subtitle: formObj.subtitle || '',
      type: procedureEquipmentListItemTypes.TITLE.value,
    };

    try {
      await dispatch(updateProcedureEquipmentList(procedureId, selectedEquipmentList?.id, { ...selectedEquipmentList, items: [...items, newItem] }));
      selectEquipmentList({ ...selectedEquipmentList, items: [...items, newItem] });

      stopLoading();
      toggleTitleModal(false);
      showAlert('success', 'Title has been successfully added');
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  const onUpdateTitle = async (formObj) => {
    startLoading();

    const items = [...selectedEquipmentList?.items];
    const _item = items.find((i) => i.id === formObj.id);

    _item.title = formObj.title || '';
    _item.subtitle = formObj.subtitle || '';

    try {
      await dispatch(updateProcedureEquipmentList(procedureId, selectedEquipmentList?.id, { ...selectedEquipmentList, items }));
      selectEquipmentList({ ...selectedEquipmentList, items });
      selectItem({ ..._item });
      stopLoading();
      showAlert('success', 'Title has been successfully updated');
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  const onUpdateItem = async (formObj) => {
    try {
      startLoading();

      const items = [...selectedEquipmentList?.items];
      const _item = items.find((i) => i.id === formObj.id);

      _item.preSelect = !!formObj.preSelect;
      _item.mandatory = !!formObj.preSelect && !!formObj.mandatory;
      _item.fixedQuantity = !!formObj.fixedQuantity;

      if (formObj?.quantity && Number(formObj?.quantity)) {
        _item.quantity = Number(formObj.quantity);
      }
      if (formObj?.min && Number(formObj?.min)) {
        _item.min = !formObj.fixedQuantity ? Number(formObj.min) : null;
      }
      if (formObj?.max && Number(formObj?.max)) {
        _item.max = !formObj.fixedQuantity ? Number(formObj.max) : null;
      }

      await dispatch(updateProcedureEquipmentList(procedureId, selectedEquipmentList?.id, { items }));
      selectEquipmentList({ ...selectedEquipmentList, items });
      selectItem({ ..._item});
      stopLoading();
      showAlert('success', 'Successfully updated');
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  const onDeleteItem = async () => {
    startDeleting();

    try {
      const items = [...selectedEquipmentList?.items].filter((i) => i.id !== selectedItem?.id);

      items.filter((i) => i.orderNumber > selectedItem.orderNumber).forEach((item) => {
        item.orderNumber = item.orderNumber - 1;
      });

      await dispatch(updateProcedureEquipmentList(procedureId, selectedEquipmentList?.id, { items }));
      selectEquipmentList({ ...selectedEquipmentList, items });

      toggleDeleteItemModal(false);
      selectItem(null);
      showAlert('success', 'Item has been successfully deleted');
    } catch (err) {
      console.error(err);
    } finally {
      stopDeleting();
    }
  };

  const onDeleteClick = () => {
    if (selectedItem) {
      return toggleDeleteItemModal(true);
    }

    if (selectedEquipmentList) {
      return toggleDeleteEquipmentListModal(true);
    }
  };

  const onDuplicateClick = async () => {
    if (!selectedEquipmentList) {
      return;
    }
    toggleDuplicateModal(true);
  };

  const onDuplicateEquipmentList = async (formObj) => {
    try {
      startDuplicateLoading();
      if (selectedEquipmentList) {
        const equipmentListObj = {
          active: true,
          name: formObj?.name || selectedEquipmentList?.name || '',
          subtitle: formObj?.subtitle || selectedEquipmentList?.subtitle || '',
          description: formObj?.description || selectedEquipmentList?.description || '',
          orderNumber: equipmentLists.length + 1,
          items: [...selectedEquipmentList?.items],
        };
        await dispatch(createProcedureEquipmentList(procedureId, equipmentListObj));
        toggleDuplicateModal(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      stopDuplicateLoading();
    }
  };

  const onReorder = async (items) => {
    dispatch(updateProcedureEquipmentList(procedureId, selectedEquipmentList?.id, { items }));
    selectEquipmentList({ ...selectedEquipmentList, items });
  };

  const onReorderLists = async (lists) => {
    dispatch(reorderEquipmentLists(procedureId, lists.map((list, index) => ({ ...list, orderNumber: index + 1 }))));
    setEquipmentLists(lists);
  };

  const renderUpdateForm = () => {
    if (selectedItem) {
      if (selectedItem.type === procedureEquipmentListItemTypes.TITLE.value) {
        return (
          <TitleForm
            initialValues={selectedItem}
            onSubmit={onUpdateTitle}
            onClose={() => toggleTitleModal(null)}
            mode='update'
            buttonText='Save Changes'
            loading={loading}
          />
        )
      }

      return (
        <EquipmentListItemForm
          initialValues={{ ...selectedItem, quantity: selectedItem?.quantity || 1 }}
          onSubmit={onUpdateItem}
          onDeleteClick={onDeleteClick}
          loading={loading}
          itemsList={itemsList}
          kitsList={kits}
        />
      );
    }

    if (selectedEquipmentList) {
      return (
        <ProcedureEquipmentListForm
          initialValues={selectedEquipmentList}
          onSubmit={onSubmitEquipmentList}
          mode='update'
          loading={loading}
        />
      );
    }

    return null;
  };

  return (
    <div className='settings-cmp__main flex-1'>
      <span className='settings-title'>{procedure ? `${procedure.name} Equipment` : 'Equipment'}</span>
      <div className='settings-cmp__body flex-1'>
        <div className='settings-block__left'>
          <EquipmentListsActions
            onAddClick={onAddClick}
            onDeleteClick={onDeleteClick}
            selectedEquipmentList={selectedEquipmentList}
            deletePermission={deletePermission}
            onDuplicateClick={onDuplicateClick}
          />
          <ProcedureEquipmentListsList
            equipmentLists={equipmentLists}
            onClick={(equipmentList) => {
              selectEquipmentList(equipmentList);
              selectItem(null);
            }}
            tenantColor={tenant.colorPrimary}
            selectedEquipmentList={selectedEquipmentList}
            onReorder={onReorderLists}
          />
        </div>
        <div className='settings-block__middle'>
          <EquipmentListsBreadcrumbs
            selectedEquipmentList={selectedEquipmentList}
            selectedItem={selectedItem}
            tenantColor={tenant.colorPrimary}
          />
          <EquipmentListItemsList
            items={selectedEquipmentList?.items}
            onClick={selectItem}
            selectedItem={selectedItem}
            tenantColor={tenant.colorPrimary}
            onReorder={onReorder}
            itemsList={itemsList}
            kitsList={kits}
          />
        </div>
        <div className='settings-block__right'>
          <Input
            type='search'
            placeholder='Search'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {renderUpdateForm()}
        </div>
      </div>

      <Modal
        open={!!equipmentListModal}
        onClose={() => toggleEquipmentListModal(null)}
      >
        <ProcedureEquipmentListForm
          onSubmit={onSubmitEquipmentList}
          onClose={() => toggleEquipmentListModal(null)}
          mode='create'
          buttonText='Add Equipment List'
          loading={loading}
        />
      </Modal>

      <AddKitsModal
        open={kitsModalOpen}
        onClose={() => toggleKitsModal(false)}
        options={kits}
        onSubmit={(kitIds) => onAddItems(kitIds, procedureEquipmentListItemTypes.KIT.value)}
        loading={loading}
      />

      <AddItemsModal
        open={itemsModalOpen}
        onClose={() => toggleItemModal(false)}
        onSubmit={(itemIds) => onAddItems(itemIds, procedureEquipmentListItemTypes.ITEM.value)}
        disabledItems={selectedEquipmentList?.items?.filter((i) => i?.type === procedureEquipmentListItemTypes.ITEM.value)?.map((i) => i?.value)}
        loading={loading}
      />

      <Modal
        open={!!titleModalOpen}
        onClose={() => toggleTitleModal(null)}
      >
        <TitleForm
          onSubmit={onAddTitle}
          onClose={() => toggleTitleModal(null)}
          mode='create'
          buttonText='Submit'
          loading={loading}
        />
      </Modal>

      <Modal
        open={duplicateModalOpen}
        onClose={() => toggleDuplicateModal(false)}
      >
        <ProcedureEquipmentListForm
          initialValues={selectedEquipmentList}
          buttonText='Duplicate'
          onSubmit={onDuplicateEquipmentList}
          loading={duplicateLoading}
          mode='create'
          onClose={() => toggleDuplicateModal(false)}
        />
      </Modal>

      <ConfirmationModal
        open={!!deleteEquipmentListModal}
        onClose={() => toggleDeleteEquipmentListModal(false)}
        onSubmit={onDeleteEquipmentList}
        title='Are you sure you want to delete this equipment list?'
        submitText='Delete'
        loading={deleting}
      />

      <ConfirmationModal
        open={!!deleteItemModal}
        onClose={() => toggleDeleteItemModal(false)}
        onSubmit={onDeleteItem}
        title='Are you sure you want to delete this equipment list item?'
        submitText='Delete'
        loading={deleting}
      />
    </div>
  );
};

export default ProcedureEquipmentListsPage;
